"use client";

/**
 * What a page shows instead of a blank screen when it crashes while
 * rendering (the route error boundaries: app/(crm)/error.tsx inside the
 * Lightning shell, so the navigation keeps working, and app/error.tsx when
 * the shell itself failed). Salesforce's wording: "Sorry to interrupt /
 * This page has an error. You might just need to refresh it."
 *
 *   <CrashScreen error={error} onRetry={retry} />            // inside the shell
 *   <CrashScreen error={error} onRetry={retry} fullPage />   // no shell around it
 */
import { useEffect } from "react";
import Link from "next/link";
import { RotateCw } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { Button, Illustration } from "@/components/lightning/primitives";

export function CrashScreen({ error, onRetry, fullPage }: { error: unknown; onRetry(): void; fullPage?: boolean }) {
  const { t } = useI18n();
  useEffect(() => {
    console.error("[crash] a page failed to render", error);
  }, [error]);
  return (
    <div data-lightning="" className={cn("flex flex-1 items-center justify-center p-4", fullPage && "min-h-dvh bg-background pt-safe pb-safe")}>
      <div role="alert" className="w-full max-w-lg rounded-sf border border-sf-border bg-sf-card px-4 py-8 text-sf-text shadow-sf-card">
        <Illustration
          variant="error"
          size="large"
          headingLevel={2}
          title={t("shell.crash.title")}
          description={t("shell.crash.body")}
          action={
            <div className="flex flex-wrap justify-center gap-2">
              <Button variant="brand" icon={RotateCw} onClick={onRetry}>
                {t("sf.actions.refresh")}
              </Button>
              <Button asChild variant="neutral">
                <Link href="/home/">{t("shell.crash.home")}</Link>
              </Button>
            </div>
          }
        />
      </div>
    </div>
  );
}
